import Environment from './environment';
import regexEscape from './regex-escape';
import { FORMAT_TYPES, FORMAT_MPEG_MIME } from '../constants';
import { isHlsMimeType } from './mime-types';
import isObject from './is-object';
import fileExtension from '../../utils/file-extension';

let sourceSupport_ = false;

/**
 * Builds an object representing the format and mime type support for the current environment
 * @return {object}
 */
export default function sourceSupport () {
  if (sourceSupport_ !== false) {
    return sourceSupport_;
  }

  const formatSupport = {};
  const mimeSupport = {};
  const audioTest = new Audio();

  const canPlayType = function (type) {
    if (typeof mimeSupport[type] !== 'undefined') {
      return mimeSupport[type];
    }

    // not supported by default
    mimeSupport[type] = false;

    // Check if the browser supports the chosen type natively
    if (audioTest.canPlayType(type).match(/probably|maybe/) !== null) {
      mimeSupport[type] = true;
    }

    // Older Opera versions don't play mpeg files event though it says it does
    if (type.match(regexEscape(FORMAT_MPEG_MIME)) !== null && Environment.browser.isOpera && Environment.browser.version.major < 33) {
      mimeSupport[type] = false;
    }

    // If the type is an HLS media type and hls.js is supported then the type is supported but through hls.js
    if (isHlsMimeType(type) && window.Hls && window.Hls.isSupported()) {
      mimeSupport[type] = true;
    }

    return mimeSupport[type];
  };

  Object.keys(FORMAT_TYPES).forEach(function (format) {
    formatSupport[format] = false;

    FORMAT_TYPES[format].forEach(function (type) {
      if (canPlayType(type)) {
        formatSupport[format] = true;
      }
    });
  });

  return sourceSupport_ = {
    formats: formatSupport,
    mimes: mimeSupport,
    canPlayType: canPlayType
  };
}

/**
 * Checks if a format can be played in the current environment
 * @param {string} format An audio source format
 * @return {boolean}
 */
export function canPlayFormat (format) {
  const support = sourceSupport();

  return support.formats[format] || false;
}

/**
 * Checks if a source can be played in the current environment
 * @param {object|string} format An audio source object or url
 * @return {boolean}
 */
export function canPlaySource (source) {
  if (isObject(source)) {
    // can we play the mime type provided
    if (source.mime) {
      return canPlayType(source.mime);
    }
    // can we play the format provided
    if (source.format) {
      return canPlayFormat(source.format);
    }
    // can we determine a format from the playback url and is it playable
    if (source.url) {
      return canPlaySource(source.url);
    }

    return false;
  } else if (typeof source === 'string') {
    // check if there is a file extension and if its supported
    const ext = fileExtension(source);
    if (ext) {
      return canPlayFormat(ext);
    }
  }

  return false;
};

/**
 * Checks if a mime type can be played in the current environment
 * @param {string} format An audio source mime type
 * @return {boolean}
 */
export function canPlayType (type) {
  const support = sourceSupport();

  return support.canPlayType(type);
}
